import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, List, ListItem } from '@mui/material';
import { FormattedMessage } from 'react-intl';

import QuestionTypeListDialog from './QuestionTypeListDialog';
import QuestionList from './QuestionList';
import QuestionEditor from './QuestionEditor';
import SortableButtons from './SortableButtons';
import { QUESTION_TYPES } from './types';
import { ColorFirstRow, ColorSecondRow } from './styles';
import { useTrialHasStarted } from './useTrialHasStarted';

const QuestionsContainer = ({
  questionType,
  mainQuestions,
  addMethod,
  udpateMethod,
  removeMethod,
  orderMethod,
}) => {
  const [openQuestionTypeListDialog, setOpenQuestionTypeListDialog] = useState(false);
  const [openQuestionEditor, setOpenQuestionEditor] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState();
  const trialHasStarted = useTrialHasStarted();

  const renderQuestionTypeList = () => {
    const doClose = () => setOpenQuestionTypeListDialog(false);

    const onClick = (type) => {
      addMethod(questionType, type);
      doClose();
    };

    return (
      <QuestionTypeListDialog
        isOpen={openQuestionTypeListDialog}
        doClose={doClose}
        onClick={onClick}
        types={QUESTION_TYPES[questionType].types}
      />
    );
  };

  // render method
  const renderQuestionEditor = () => {
    if (!openQuestionEditor) {
      return '';
    }

    const question = mainQuestions.find((q) => q.id === selectedQuestion);
    if (!question) {
      return '';
    }

    const doClose = () => {
      setOpenQuestionEditor(false);
    };

    const onSave = (udpatedQuestion, questionId) => {
      doClose();
      udpateMethod(questionType, udpatedQuestion, questionId);
    };

    const onDelete = (questionId) => {
      doClose();
      removeMethod(questionType, questionId);
    };

    return (
      <QuestionEditor
        isOpen={openQuestionEditor}
        onSave={onSave}
        onDelete={onDelete}
        onCancel={doClose}
        question={question}
        questionTypeParent={questionType}
      />
    );
  };

  const renderQuestionsContainer = () => {
    if (mainQuestions.length === 0) {
      return '';
    }
    return (
      <div className="pa2">
        <List>
          {mainQuestions.map((question, _index) => (
            <ListItem
              key={question.id}
              button
              onClick={() => {
                setSelectedQuestion(question.id);
                setOpenQuestionEditor(true);
              }}
              sx={{ backgroundColor: `${_index % 2 ? ColorSecondRow : ColorFirstRow}` }}
            >
              <SortableButtons
                index={_index}
                indexUp={_index + 1 === mainQuestions.length ? _index : _index + 1}
                indexDown={_index - 1 < 0 ? 0 : _index - 1}
                onAnyButtonClick={(fromIdx, toIdx) => orderMethod(questionType, fromIdx, toIdx)}
                disabled={trialHasStarted}
              />
              <QuestionList
                question={question}
                index={_index}
                required={question.required}
                onDelete={(questionId) => removeMethod(questionType, questionId)}
              />
            </ListItem>
          ))}
        </List>
        {renderQuestionEditor()}
      </div>
    );
  };

  return (
    <>
      {renderQuestionsContainer()}
      <div className="pa2">
        <Button
          onClick={() => setOpenQuestionTypeListDialog(true)}
          variant="contained"
          color="primary"
          disabled={trialHasStarted}
        >
          <FormattedMessage id={QUESTION_TYPES[questionType].buttonLabelid} />
        </Button>
        {renderQuestionTypeList()}
      </div>
    </>
  );
};

export default QuestionsContainer;

QuestionsContainer.defaultProps = {
  mainQuestions: [],
  orderMethod: () => undefined,
};

QuestionsContainer.propTypes = {
  questionType: PropTypes.string.isRequired,
  mainQuestions: PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.string,
    PropTypes.object,
    PropTypes.object,
  ])),
  addMethod: PropTypes.func.isRequired,
  udpateMethod: PropTypes.func.isRequired,
  removeMethod: PropTypes.func.isRequired,
  orderMethod: PropTypes.func,
};
